import { faLinkSlash, faPlus, faStar, faTag } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../../components/features/common";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../../components/UI/Declarations";
import { PagedList } from "../../../components/UI/PagedList/PagedList";
import type { ISiteOption } from "../../../customTypings/haulier/Haulier";
import type { ICustomerLocationAssociation } from "../../../customTypings/location/Location";
import { locationsService } from "../../../services/locations/locations.service";
import { sitesService } from "../../../services/sites/sites.service";
import { getUserFacingErrorMessage, logErrorForDev, PAGED_LIST_MAIN_LOCATION_ROW_INDICATOR, showToastError, showToastSuccess } from "../../../utils";
import { useLocationEditOutletContext } from "../locationEditOutletContext";
import { AddCustomerAssociationModal } from "./AddCustomerAssociationModal";
import { EditCustomerAssociationLabelModal } from "./EditCustomerAssociationLabelModal";

export interface LocationCustomerAssociationsViewProps {
    locationId: string;
    metaDeleted: boolean;
}

const LocationCustomerAssociationsView = ({
    locationId,
    metaDeleted,
}: LocationCustomerAssociationsViewProps) => {
    const navigate = useNavigate();
    const { locationName } = useLocationEditOutletContext();
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [refreshNumber, setRefreshNumber] = useState(0);
    const [showAddModal, setShowAddModal] = useState(false);
    const [confirmDissociateRow, setConfirmDissociateRow] = useState<ICustomerLocationAssociation | null>(null);
    const [confirmSetMainRow, setConfirmSetMainRow] = useState<ICustomerLocationAssociation | null>(null);
    const [editLabelRow, setEditLabelRow] = useState<ICustomerLocationAssociation | null>(null);

    const showSiteColumn = useMemo(() => sites.length > 1, [sites.length]);
    const singleSiteId = sites.length === 1 ? sites[0].id : null;

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then(setSites)
            .catch((e: unknown) => logErrorForDev("LocationCustomerAssociationsView.sites", e));
    }, []);

    const columns: IColumn[] = useMemo(() => {
        const base: IColumn[] = [
            { member: "customerName", renderAs: ColumnRender.text, title: "Customer", width: 220 } as IColumn,
        ];
        if (showSiteColumn) {
            base.push({ member: "siteName", renderAs: ColumnRender.text, title: "Site", width: 160 } as IColumn);
        }
        base.push(
            { member: "label", renderAs: ColumnRender.text, title: "Label", width: 160 } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn
        );
        return base;
    }, [showSiteColumn]);

    const filters: IFilter[] = useMemo(() => {
        const list: IFilter[] = [
            { title: "Customer", member: "customerNameSearch", type: FilterType.text } as IFilter,
        ];
        if (showSiteColumn) {
            list.push({ title: "Site", member: "siteNameSearch", type: FilterType.text } as IFilter);
        }
        list.push(
            { title: "Label", member: "labelSearch", type: FilterType.text } as IFilter,
            {
                title: "Deleted",
                member: "customerIsDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
            {
                title: "Enabled",
                member: "customerIsEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter
        );
        return list;
    }, [showSiteColumn]);

    const headActions: IPagedListAction[] = useMemo(
        () =>
            metaDeleted
                ? []
                : [
                      {
                          text: "Add Customer",
                          icon: faPlus,
                          primary: true,
                          onClick: () => setShowAddModal(true),
                      } as IPagedListAction,
                  ],
        [metaDeleted]
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: ICustomerLocationAssociation) => navigate(`/customers/${item.customerId}`),
                primary: true,
            } as IPagedListAction,
            ...(metaDeleted
                ? []
                : [
                      {
                          text: "Edit label",
                          icon: faTag,
                          hideWhenMember: "isDeleted",
                          onClick: (item: ICustomerLocationAssociation) => setEditLabelRow(item),
                      } as IPagedListAction,
                      {
                          text: "Set as primary address",
                          icon: faStar,
                          hideWhenMember: "isMainLocation",
                          onClick: (item: ICustomerLocationAssociation) => setConfirmSetMainRow(item),
                      } as IPagedListAction,
                      {
                          text: "Unlink",
                          icon: faLinkSlash,
                          itemClassName: "text-danger",
                          onClick: (item: ICustomerLocationAssociation) => setConfirmDissociateRow(item),
                      } as IPagedListAction,
                  ]),
        ],
        [metaDeleted, navigate]
    );

    const runDissociate = () => {
        if (!confirmDissociateRow) return;
        locationsService
            .softDeleteCustomerAssociation(locationId, confirmDissociateRow.id)
            .then(() => {
                showToastSuccess("Customer unlinked.");
                setRefreshNumber((n) => n + 1);
                setConfirmDissociateRow(null);
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not unlink customer."));
                setConfirmDissociateRow(null);
            });
    };

    const runSetMain = () => {
        if (!confirmSetMainRow) return;
        locationsService
            .setMainCustomerAssociation(locationId, confirmSetMainRow.id)
            .then(() => {
                showToastSuccess("Primary address updated.");
                setRefreshNumber((n) => n + 1);
                setConfirmSetMainRow(null);
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not set primary address."));
                setConfirmSetMainRow(null);
            });
    };

    return (
        <div className="mt-3">
            <h3 className="mb-3">{locationName} Customers</h3>
            {metaDeleted && (
                <Alert variant="warning" className="mb-3">
                    This location is deleted. Restore it before adding or changing linked customers.
                </Alert>
            )}

            <PagedList
                key={showSiteColumn ? "multi-site" : "single-site"}
                columns={columns}
                headActions={headActions}
                itemActions={itemActions}
                filters={filters}
                staticFilterValues={{ isDeleted: false }}
                showDeletedRowIndicator
                rowIndicators={[PAGED_LIST_MAIN_LOCATION_ROW_INDICATOR]}
                defaultSortBy="isMainLocation"
                defaultSortOrder={SortOrder.descending}
                getUrl={`/api/locations/${encodeURIComponent(locationId)}/customer-associations/all`}
                keyColumn="id"
                rowFormatter={(item: ICustomerLocationAssociation) => item.customerName}
                refreshNumber={refreshNumber}
                persistanceKey={`LocationCustomerAssociationsPage.${locationId}`}
                rowActionsAriaLabel="Customer link actions"
            />

            <AddCustomerAssociationModal
                show={showAddModal}
                locationId={locationId}
                showSitePicker={showSiteColumn}
                defaultSiteId={singleSiteId}
                onClose={() => setShowAddModal(false)}
                onAssociated={() => {
                    setShowAddModal(false);
                    setRefreshNumber((n) => n + 1);
                }}
            />

            <EditCustomerAssociationLabelModal
                show={editLabelRow !== null}
                locationId={locationId}
                association={
                    editLabelRow
                        ? {
                              id: editLabelRow.id,
                              customerName: editLabelRow.customerName,
                              label: editLabelRow.label,
                          }
                        : null
                }
                onClose={() => setEditLabelRow(null)}
                onSaved={() => setRefreshNumber((n) => n + 1)}
            />

            <ConfirmationModel
                show={confirmDissociateRow !== null}
                onClose={(confirmed) => {
                    if (confirmed) runDissociate();
                    else setConfirmDissociateRow(null);
                }}
                title="Unlink customer"
                body={
                    confirmDissociateRow
                        ? `Remove the link to "${confirmDissociateRow.customerName}" from this location?`
                        : ""
                }
                buttonSuccessVariant="danger"
                buttonSuccessText="Unlink"
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={confirmSetMainRow !== null}
                onClose={(confirmed) => {
                    if (confirmed) runSetMain();
                    else setConfirmSetMainRow(null);
                }}
                title="Set primary address"
                body={
                    confirmSetMainRow
                        ? `Set this location as the primary address for "${confirmSetMainRow.customerName}"? Any existing primary address for that customer will be cleared.`
                        : ""
                }
                buttonSuccessText="Set as primary"
                buttonCancelText="Cancel"
            />
        </div>
    );
};

export { LocationCustomerAssociationsView };
